/**
 * Typed wrapper for the `/salescoach/api/jira/catalog` endpoint
 * (Plan Jira-tab Task A3).
 *
 * Mirrors `engagement-api.ts` conventions: build the URL through `buildApiUrl`
 * and return the raw `ApiEnvelope<T>` — unwrapping is the hook's job
 * (`use-jira.ts`).
 *
 * The catalog is static (no filter bar), but `buildApiUrl` always requires a
 * `Filters` arg — it calls `resolveRange` / `stripDefaults` unconditionally.
 * We pass `DEFAULT_FILTERS`; `stripDefaults` removes every default key, so the
 * only query params emitted are the resolved `from`/`to`, which the
 * filter-free catalog route ignores. The path stays
 * `/salescoach/api/jira/catalog`.
 */

import { apiFetch } from '@/services/api';
import { buildApiUrl } from '@/services/api-client';
import { DEFAULT_FILTERS, type Filters } from '@/lib/filters-schema';
import type {
  ApiEnvelope,
  JiraCatalogView,
  JiraGaps,
  JiraUsageCount,
  JiraUsageRow,
} from './api-types';

export async function getJiraCatalog(): Promise<ApiEnvelope<JiraCatalogView>> {
  return apiFetch<ApiEnvelope<JiraCatalogView>>(
    buildApiUrl('/salescoach/api/jira/catalog', DEFAULT_FILTERS),
  );
}

/**
 * Coverage gaps — UNLIKE the catalog, this respects the global FilterBar:
 * `buildApiUrl` resolves `from`/`to` from the preset and emits the active
 * `role` so the trace-derived gap lists are scoped to the selected window.
 */
export async function getJiraGaps(
  filters: Filters,
): Promise<ApiEnvelope<JiraGaps>> {
  return apiFetch<ApiEnvelope<JiraGaps>>(
    buildApiUrl('/salescoach/api/jira/gaps', filters),
  );
}

/**
 * Per-request-type usage counters — filter-aware, mirrors `getJiraGaps`. The
 * catalog table joins these onto its rows by `requestTypeId` to show
 * drafts/clicks columns scoped to the selected window + role.
 */
export async function getJiraUsage(
  filters: Filters,
): Promise<ApiEnvelope<JiraUsageCount[]>> {
  return apiFetch<ApiEnvelope<JiraUsageCount[]>>(
    buildApiUrl('/salescoach/api/jira/usage', filters),
  );
}

/**
 * Per-type usage drill-down — the trace rows for a single request type. Same
 * filter-aware `buildApiUrl` mechanism as the others, but the `requestTypeId`
 * is part of the PATH (`/usage/:requestTypeId`) while the active filters ride
 * in the query string so the drill-down respects the global FilterBar.
 */
export async function getJiraTypeUsages(
  requestTypeId: number,
  filters: Filters,
): Promise<ApiEnvelope<JiraUsageRow[]>> {
  return apiFetch<ApiEnvelope<JiraUsageRow[]>>(
    buildApiUrl(`/salescoach/api/jira/usage/${requestTypeId}`, filters),
  );
}
